"use client";

import cn from "classnames";

import { PhoneIcon } from "@/components/icons/phone.icon";
import { Link } from "@/components/link/link.component";
import { formatUsPhoneNumber } from "@/components/phoneInput/phoneInput.component";

import { toTelHref } from "@/utils/string.util";

import type { ButtonVariant } from "@/components/button/buttonVariants";
import { useCampaign } from "@/contexts/campaignProvider.context";
import { useSettings } from "@/contexts/settingsProvider.context";

const DEFAULT_CTA_CLASS = "w-fit min-w-40 px-10 py-3 uppercase";

/** Primary or secondary hero CTA supporting link and Call Now (phone) modes. */
export function HeroSectionCta({
  href,
  label,
  openInNewTab,
  variant,
  primary,
  secondary,
  border,
  phone = false,
  phoneNumber,
  className,
  testId = "hero-section-cta",
}: HeroSectionCtaProps) {
  const settings = useSettings();
  const campaign = useCampaign();
  const layoutClassName = cn(DEFAULT_CTA_CLASS, className);

  if (phone) {
    const desktopPhoneNumber =
      campaign?.tfnDesktop ?? settings?.phoneNumber ?? phoneNumber ?? null;
    const mobilePhoneNumber =
      campaign?.tfnMobile ?? settings?.phoneNumber ?? phoneNumber ?? null;

    if (!desktopPhoneNumber && !mobilePhoneNumber) return null;

    return (
      <>
        {mobilePhoneNumber ? (
          <PhoneCtaLink
            phoneNumber={mobilePhoneNumber}
            label={label}
            variant={variant}
            primary={primary}
            secondary={secondary}
            border={border}
            className={cn(layoutClassName, "flex lg:hidden")}
            testId={testId}
          />
        ) : null}
        {desktopPhoneNumber ? (
          <PhoneCtaLink
            phoneNumber={desktopPhoneNumber}
            label={label}
            variant={variant}
            primary={primary}
            secondary={secondary}
            border={border}
            className={cn(layoutClassName, "hidden lg:flex")}
            testId={testId}
          />
        ) : null}
      </>
    );
  }

  if (!href || !label) return null;

  return (
    <Link
      data-testid={testId}
      href={href}
      variant={variant}
      primary={primary}
      secondary={secondary}
      border={border}
      className={layoutClassName}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noopener noreferrer" : undefined}>
      {label}
    </Link>
  );
}

export type HeroSectionCtaProps = {
  href?: string | null;
  label?: string | null;
  openInNewTab?: boolean | null;
  /** Hero cyan variants; when set, takes precedence over primary/secondary/border */
  variant?: ButtonVariant;
  primary?: boolean;
  secondary?: boolean;
  border?: boolean;
  phone?: boolean;
  phoneNumber?: string | null;
  className?: string;
  testId?: string;
};

const PhoneCtaLink = ({
  phoneNumber,
  label,
  variant,
  primary,
  secondary,
  border,
  className,
  testId,
}: {
  phoneNumber: string;
  label?: string | null;
  variant?: ButtonVariant;
  primary?: boolean;
  secondary?: boolean;
  border?: boolean;
  className?: string;
  testId: string;
}) => {
  const href = toTelHref(phoneNumber);
  if (!href) return null;

  const formatted = formatUsPhoneNumber(phoneNumber) || phoneNumber;
  const trimmedLabel = label?.trim();

  return (
    <Link
      data-testid={testId}
      href={href}
      variant={variant}
      primary={primary}
      secondary={secondary}
      border={border}
      aria-label={
        trimmedLabel ? `${trimmedLabel} ${formatted}` : `Call ${formatted}`
      }
      className={className}>
      {trimmedLabel ? (
        trimmedLabel
      ) : (
        <>
          <PhoneIcon width={22} height={22} aria-hidden className="shrink-0" />
          {formatted}
        </>
      )}
    </Link>
  );
};
